import React, { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Building2, CreditCard, CheckCircle, Clock, AlertCircle,
  ArrowUpRight, ArrowDownLeft, RefreshCw, Shield, Wallet,
  FileText, Eye, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = getApiBaseUrl();

const LandlordBankingOverview = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState('pending'); // pending, in_progress, verified

  const fetchOverview = useCallback(async () => {
    if (!user?.id) return;
    
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/griffin/landlord/${user.id}/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
        
        // Determine onboarding status
        if (data.legal_person?.decision_outcome === 'accepted') {
          setOnboardingStatus('verified');
        } else if (data.legal_person) {
          setOnboardingStatus('in_progress');
        } else {
          setOnboardingStatus('pending');
        }
      }
    } catch (error) {
      console.error('Error fetching banking overview:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const startOnboarding = async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/griffin/onboard/landlord`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.id,
          display_name: `${user.first_name} ${user.last_name}`,
          given_name: user.first_name,
          surname: user.last_name,
          email: user.email,
          is_company: false,
          start_verification: true,
          create_account: true
        })
      });
      
      if (response.ok) {
        toast.success('Banking onboarding started!');
        fetchOverview();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start onboarding');
      }
    } catch (error) {
      console.error('Error starting onboarding:', error);
      toast.error('Error starting onboarding');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Verified
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-amber-100 text-amber-800">
            <Clock className="w-3 h-3 mr-1" />
            Verification In Progress
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            Not Started
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6" data-testid="landlord-banking-overview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Banking & Payments
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Manage your client money accounts and payments through Griffin
          </p>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(onboardingStatus)}
          <Button variant="outline" size="sm" onClick={fetchOverview}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Onboarding Required Card */}
      {onboardingStatus === 'pending' && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">Complete Banking Setup</h3>
                <p className="text-sm text-blue-800 mt-1">
                  Complete identity verification to enable rent collection, deposit management, 
                  and contractor payments through your dedicated client money account.
                </p>
                <div className="mt-4 flex gap-3">
                  <Button onClick={startOnboarding}>
                    Start Verification
                  </Button>
                  <Button variant="outline">
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verification In Progress Card */}
      {onboardingStatus === 'in_progress' && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">Verification In Progress</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Your identity is being verified. This typically takes 1-2 business days.
                  We'll notify you once verification is complete.
                </p>
                {overview?.legal_person?.verification_status && (
                  <div className="mt-3">
                    <Badge variant="outline" className="bg-white">
                      Status: {overview.legal_person.verification_status}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Overview (when verified) */}
      {onboardingStatus === 'verified' && overview?.accounts?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Total Balance */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Total Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                £{parseFloat(overview.total_balance || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Across {overview.accounts.length} account{overview.accounts.length > 1 ? 's' : ''}
              </p>
            </CardContent>
          </Card>

          {/* Account Cards */}
          {overview.accounts.map((account) => (
            <Card key={account.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-700">
                    {account.display_name}
                  </CardTitle>
                  <Badge 
                    className={account.account_status === 'open' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'}
                  >
                    {account.account_status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-semibold">
                  £{parseFloat(account.available_balance || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
                
                {/* Bank Details */}
                {account.sort_code && account.account_number && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Sort Code</span>
                      <span className="font-mono flex items-center gap-1">
                        {account.sort_code}
                        <button 
                          onClick={() => copyToClipboard(account.sort_code)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Account No.</span>
                      <span className="font-mono flex items-center gap-1">
                        {account.account_number}
                        <button 
                          onClick={() => copyToClipboard(account.account_number)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </span>
                    </div>
                    {account.iban && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">IBAN</span>
                        <span className="font-mono text-xs flex items-center gap-1">
                          {account.iban.slice(0, 8)}...
                          <button 
                            onClick={() => copyToClipboard(account.iban)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Recent Payments */}
      {overview?.recent_payments?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Recent Payments
            </CardTitle>
            <CardDescription>Last 10 transactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overview.recent_payments.map((payment) => (
                <div 
                  key={payment.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {payment.payment_direction === 'inbound-payment' ? (
                      <div className="p-2 bg-green-100 rounded-full">
                        <ArrowDownLeft className="w-4 h-4 text-green-600" />
                      </div>
                    ) : (
                      <div className="p-2 bg-blue-100 rounded-full">
                        <ArrowUpRight className="w-4 h-4 text-blue-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">
                        {payment.payment_reference || payment.payment_type}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(payment.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      payment.payment_direction === 'inbound-payment' 
                        ? 'text-green-600' 
                        : 'text-gray-900'
                    }`}>
                      {payment.payment_direction === 'inbound-payment' ? '+' : '-'}
                      £{parseFloat(payment.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </p>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        payment.payment_status === 'delivered' 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {payment.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State for Verified but No Accounts */}
      {onboardingStatus === 'verified' && (!overview?.accounts || overview.accounts.length === 0) && (
        <Card>
          <CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="font-semibold text-gray-900">No Accounts Yet</h3>
            <p className="text-sm text-gray-500 mt-2">
              Your verification is complete. Bank accounts will be created when you add properties.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LandlordBankingOverview;
